// Wire types — mirror the ac-edge tenant API (`workers/ac-edge/src/index.ts`).
//
// Request bodies are camelCase ONLY where the worker reads camelCase; the tenant
// API in fact reads snake_case bodies (e.g. {subject, priority_code, project_id}),
// so most request shapes here are snake_case to match. RESPONSES are snake_case
// rows as returned by D1. Timestamps are UNIX SECONDS (see ARCHITECTURE §0).

// ---------------------------------------------------------------------------
// Auth (POST /api/auth/login, GET /api/auth/me, POST /api/auth/logout)
// ---------------------------------------------------------------------------

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  /** Profile photo URL shown in avatars; null = render initials. */
  avatar_url?: string | null;
  /** The department the signed-in user belongs to (0100); null = none. Display
   *  only — Change-Management access is enforced server-side. */
  department_id?: string | null;
}

export interface LoginResponse {
  token: string;
  user: AuthUser;
}

export interface MeResponse {
  user: AuthUser;
  roles: string[];
  /** Resolved permission codes — the SPA gates modules (nav/buttons) on these;
   *  server guards remain the source of truth. */
  permissions?: string[];
  is_admin?: boolean;
  /** Facility group (campus) names the user belongs to — read-only display. */
  groups?: string[];
  /** Plan entitlements (0027/tiers) — the SPA hides/disables gated features;
   *  server gates remain the source of truth. */
  plan?: {
    code: string;
    name: string;
    features: Record<string, boolean>;
    limits: { seats: number; projects: number; storageMb: number };
  };
  /** Maintenance mode — when enabled, the app shell shows the message across all
   *  modules for non-admins; admins keep access so they can turn it off. */
  maintenance?: { enabled: boolean; message: string | null };
}

// ---------------------------------------------------------------------------
// Roles & permissions (granular module access; Settings → Roles, admin only)
// ---------------------------------------------------------------------------

export interface RoleRow {
  id: string;
  code: string;
  name: string;
  is_system: number;
  /** Whether holding this role grants tenant-app sign-in (0027). 1 = yes. */
  grants_access?: number;
  permissions: string[];
  member_count: number;
}
export interface RolesResponse {
  roles: RoleRow[];
}
export interface PermissionsResponse {
  permissions: { id: string; code: string }[];
}

export interface TenantUser {
  id: string;
  name: string;
  email: string;
  status: string;
  /** Profile photo URL; null = render initials. */
  avatar_url?: string | null;
  /** Comma-joined role codes (e.g. "admin" or "agent,viewer"); null = none. */
  roles?: string | null;
  /** Reporting hierarchy: who this user reports to (0026). */
  manager_id?: string | null;
  manager_name?: string | null;
  /** The department this user belongs to (0100); scopes Change-Management. */
  department_id?: string | null;
  department_name?: string | null;
  /** Site-access gate (0027). access_override: null=inherit roles, 1=allow, 0=deny.
   *  can_access is the resolved effective answer (1 = may sign in). */
  access_override?: number | null;
  can_access?: number;
}
export interface TenantUsersResponse {
  users: TenantUser[];
}

// ---------------------------------------------------------------------------
// Tickets (helpdesk)
// ---------------------------------------------------------------------------

/** Seeded ticket status codes (0004_seed_defaults). Should come from a settings
 *  endpoint later; hard-coded here for this slice. */
export type TicketStatusCode = "open" | "pending" | "solved" | "closed";
/** Seeded ticket priority codes. */
export type TicketPriorityCode = "low" | "normal" | "high" | "urgent";

/** A tag attached to a ticket or task (generic tag set). */
export interface TagRow {
  id: string;
  name: string;
  color: string | null;
}

export interface TicketRow {
  id: string;
  subject: string;
  /** NOTE: the list/detail endpoints run `SELECT tickets.*`, which returns the
   *  raw status_id/priority_id FKs but NOT codes. status_code/priority_code are
   *  populated only on enriched rows (e.g. the time-tracking joins); on the
   *  inbox/board/detail rows they are absent at runtime and the code must be
   *  resolved via the statuses/priorities lookup (id → code). The string typing
   *  below is retained for back-compat; treat the value as possibly-empty. */
  status_id?: string;
  priority_id?: string;
  status_code: string;
  priority_code: string;
  assignee_id: string | null;
  requester_id: string | null;
  project_id: string | null;
  source: string | null;
  created_at: number;
  updated_at: number;
  // 0017 higher-ed fields (nullable free-text).
  department?: string | null;
  term?: string | null;
  applicant_ref?: string | null;
  // Some list queries denormalize a display name / number — tolerated if absent.
  number?: number | null;
  assignee_name?: string | null;
  /** 0015 intake: free-text "client" the request is on behalf of (non-portal). */
  requester_name?: string | null;
  requester_email?: string | null;
  /** 0014 helpdesk fields. `due_date` is the ticket's target/resolution due
   *  (unix seconds); `first_replied_at` / `last_reply_at` track conversation
   *  activity. The list endpoint runs `SELECT * FROM tickets`, so these raw
   *  columns ARE present even though only the detail/SLA endpoints type them
   *  formally. NOTE: the per-policy SLA-timer due times (first_response /
   *  resolution from ticket_sla_timers) are NOT joined into the list — only the
   *  ticket's own `due_date` is available here. */
  due_date?: number | null;
  first_replied_at?: number | null;
  last_reply_at?: number | null;
  /** Derived by listTickets: the soonest unsatisfied SLA timer (real
   *  first-response/resolution due), so the inbox shows a true countdown. */
  sla_due_at?: number | null;
  sla_kind?: string | null; // first_response | resolution
  sla_breached?: number | null; // 0/1
  /** Derived by listTickets: comma-joined tag names for the inbox row strip. */
  tag_names?: string | null;
  /** 0036: structured affected-asset link (resolve name via the assets list). */
  asset_id?: string | null;
  /** Ticket type code (question|incident|service_request|problem or tenant types). */
  type?: string | null;
  /** 0112 CSAT: satisfaction captured on the ticket (survey or agent-set). */
  csat_rating?: number | null;
  csat_comment?: string | null;
  csat_responded_at?: number | null;
  csat_source?: string | null; // 'survey' | 'agent'
}

/** Favorites (0113): per-user starred tickets, change orders + work orders. */
export type FavoriteType = "ticket" | "change" | "facility";
export interface FavoriteIds {
  tickets: string[];
  changes: string[];
  facilities: string[];
}

/** CSAT survey configuration (0112) — Settings → Helpdesk → Satisfaction. */
export interface CsatConfig {
  enabled: boolean;
  trigger_statuses: string[];
  question: string;
  labels: string[];
  thankyou: string;
  comment_label: string;
  comment_placeholder: string;
  submit_label: string;
  delay_hours: number;
}
export interface CsatConfigResponse {
  config: CsatConfig;
}

/** One answered survey in the Settings → Satisfaction → History list. */
export interface CsatResponseRow {
  id: string;
  number: number;
  number_display?: string;
  subject: string | null;
  requester_name: string | null;
  requester_email: string | null;
  rating: number;
  comment: string | null;
  responded_at: number;
}
/** Rating buckets shared by the history filter, the table and the summary tiles:
 *  bad = 1-2, ok = 3, great = 4-5. */
export type CsatBucket = "bad" | "ok" | "great";
/** Aggregates for the selected PERIOD — never narrowed by the bucket filter. */
export interface CsatSummary {
  rated: number;
  great: number;
  ok: number;
  bad: number;
  avg_rating: number | null;
  csat_pct: number;
}
export interface CsatResponsesPage {
  rows: CsatResponseRow[];
  total: number;
  limit: number;
  offset: number;
  /** Null when the caller opted out of the aggregate (export chunks). */
  summary: CsatSummary | null;
}

/** Public (anonymous) survey payload for the /csat/:token rating page. */
export interface CsatPublic {
  number: string;
  subject: string;
  already_responded: boolean;
  current_rating: number | null;
  current_comment: string | null;
  question: string;
  labels: string[];
  thankyou: string;
  comment_label: string;
  comment_placeholder: string;
  submit_label: string;
  branding: Branding;
}

export interface TicketCommentRow {
  id: string;
  ticket_id: string;
  author_id: string | null;
  author_name?: string | null;
  body: string;
  is_internal: number;
  created_at: number;
}

export interface AttachmentRow {
  id: string;
  r2_key: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  parent_type: string;
  parent_id: string;
  created_at: number;
}

/** A row in the entity-links graph (escalated_to / tracked_in / related…). */
export interface EntityLinkRow {
  id: string;
  source_type: string;
  source_id: string;
  target_type: string;
  target_id: string;
  relation: string;
  created_at?: number;
  // Convenience label the worker may include for display.
  target_label?: string | null;
}

/** Detail returned by GET /api/tickets/:id → {ticket:{...,comments,attachments},links}. */
export interface TicketDetail extends TicketRow {
  /** 0023 multi-sets: full ordered lists; position 0 mirrors the single columns. */
  assignees?: { user_id: string; name: string }[];
  requesters?: { id: string; name: string; email: string | null }[];
  comments: TicketCommentRow[];
  attachments: AttachmentRow[];
  tags?: TagRow[];
}

export interface TicketsResponse {
  tickets: TicketRow[];
}
export interface TicketResponse {
  ticket: TicketRow;
}
export interface TicketDetailResponse {
  ticket: TicketDetail;
  links: EntityLinkRow[];
}
export interface CommentResponse {
  comment: TicketCommentRow;
}

/** One row of the per-entity activity timeline (audit_log + actor name). */
export interface ActivityEventRow {
  id: string;
  actor_id: string | null;
  actor_name: string | null;
  actor_email: string | null;
  action: string;
  entity_type: string;
  entity_id: string;
  before_json: string | null;
  after_json: string | null;
  created_at: number;
}
export interface ActivityResponse {
  events: ActivityEventRow[];
}

/** One hours-worked entry in a ticket's time log (0032). */
export interface TicketTimeLog {
  id: string;
  ticket_id: string;
  user_id: string | null;
  user_name: string;
  minutes: number;
  note: string | null;
  logged_at: number;
  created_at: number;
}
export interface TicketTimeResponse {
  logs: TicketTimeLog[];
  total_minutes: number;
}

export interface CreateTicketInput {
  subject: string;
  description?: string;
  priority_code?: string;
  project_id?: string;
  /** Technician the ticket is assigned to. */
  assignee_id?: string;
  /** "Client" the request is on behalf of (free-text, non-portal). */
  requester_name?: string;
  requester_email?: string;
  /** 0017 higher-ed fields (free-text, nullable). */
  department?: string;
  term?: string;
  applicant_ref?: string;
  /** Tag names to attach on create (replace set). */
  tags?: string[];
  /** 0036: structured affected-asset link. */
  asset_id?: string;
  /** Intake email opt-outs (default true): email the client / the technician. */
  notify_requester?: boolean;
  notify_assignee?: boolean;
}

/** The reusable ticket settings a template captures (0108). Never includes the
 *  assignee, requester, or comments. */
export interface TicketTemplateData {
  subject?: string | null;
  description?: string | null;
  priority_code?: string | null;
  type?: string | null;
  category_id?: string | null;
  department?: string | null;
  source?: string | null;
  campus?: string | null;
  building?: string | null;
  room?: string | null;
}

/** A saved ticket template (0108). is_shared=1 → usable by any agent on create. */
export interface TicketTemplate {
  id: string;
  name: string;
  created_by: string | null;
  created_by_name: string | null;
  is_shared: number;
  data: TicketTemplateData;
  created_at: number;
  updated_at: number;
}
export interface TicketTemplatesResponse {
  templates: TicketTemplate[];
}
export interface TicketTemplateResponse {
  template: TicketTemplate;
}

/** Minimal roster row for assignment pickers (GET /api/assignees). */
export interface Assignee {
  id: string;
  name: string;
  email: string;
}
export interface AssigneesResponse {
  assignees: Assignee[];
}

/** Role-visibility per work type & people field (Settings → Picker Visibility). */
export type WorkTypeId = "tickets" | "projects" | "action_items" | "work_orders" | "change_management";
export type PeopleFieldId = "requester" | "assignee" | "shared" | "contributor";
export interface RoleVisibilityResponse {
  /** key = "<work_type>:<field>" → allowed role codes. Empty/absent = all roles. */
  matrix: Record<string, string[]>;
}

/** New-ticket intake autocomplete (GET /api/clients): prior clients + depts. */
export interface ClientSuggestion {
  name: string;
  email: string | null;
}
export interface ClientsResponse {
  clients: ClientSuggestion[];
  departments: string[];
}
export interface UpdateTicketInput {
  /** 0023 multi-sets (full replace; position 0 becomes the primary). */
  assignee_ids?: string[];
  requesters?: { name: string; email?: string | null }[];
  subject?: string;
  status_code?: string;
  priority_code?: string;
  assignee_id?: string | null;
  /** Client (requester) correction — name/email shown across the inbox. */
  requester_id?: string | null;
  requester_name?: string | null;
  requester_email?: string | null;
  /** 0017 higher-ed fields (validated when present; null clears). */
  department?: string | null;
  term?: string | null;
  applicant_ref?: string | null;
  /** 0036: structured affected-asset link (null clears). */
  asset_id?: string | null;
}
export interface AddCommentInput {
  body: string;
  is_internal?: boolean;
  /** Minutes worked, logged alongside the comment (0023/0025). */
  time_spent_mins?: number;
  /** Email recipients for this reply. When omitted the server keeps the legacy
   *  behaviour: a public reply emails the requester, an internal note emails
   *  no one. Internal notes never email the requester regardless of the flag. */
  notify_requester?: boolean;
  notify_assignee?: boolean;
  /** @mention recipients (resolved user ids) to notify. */
  mentions?: string[];
}
export interface EscalateInput {
  project_id: string;
  title?: string;
  assignee_id?: string;
}

/** Body for escalating a task into a ticket (inverse of EscalateInput). */
export interface CreateTicketFromTaskInput {
  subject?: string;
  priority_code?: string;
  assignee_id?: string | null;
}

export interface CannedResponseRow {
  id: string;
  title: string;
  body: string;
  created_at?: number;
}
export interface CannedResponsesResponse {
  canned: CannedResponseRow[];
}

// ---------------------------------------------------------------------------
// Projects / boards / tasks (work management)
// ---------------------------------------------------------------------------

export interface ProjectRow {
  id: string;
  key: string;
  name: string;
  description: string | null;
  archived: number;
  /** 'shared' (default) or 'private' (members + admins only). Added in 0013. */
  visibility?: ProjectVisibility;
  lead_id?: string | null;
  // 0016 metadata + list enrichment:
  icon?: string | null;
  color?: string | null;
  status?: string; // not_started|on_track|at_risk|delayed|on_hold|done
  priority?: string; // low|medium|high|urgent
  start_date?: number | null;
  end_date?: number | null;
  /** 0020 higher-ed: academic term code (see /api/config/terms). */
  term?: string | null;
  /** 0081: Microsoft Teams Incoming Webhook URL (null = unlinked). */
  teams_webhook_url?: string | null;
  /** 0083: JSON of header/view customization (compact, header note, sections). */
  ui_config?: string | null;
  /** Enriched by GET /api/projects (not stored). */
  task_count?: number;
  done_count?: number;
  lead_name?: string | null;
  created_at: number;
  updated_at: number;
}

export interface TaskRow {
  id: string;
  project_id: string;
  board_id: string | null;
  column_id: string | null;
  number?: number | null;
  parent_id?: string | null;
  title: string;
  description?: string | null;
  status_code?: string | null;
  /** Raw status column (present on un-enriched rows, e.g. calendar view). */
  status?: string | null;
  assignee_id: string | null;
  assignee_name?: string | null;
  /** Some enriched rows expose a denormalized code; raw task rows carry priority_id. */
  priority_code?: string | null;
  /** 0017 built-in fields (raw task rows from SELECT * carry these). */
  priority_id?: string | null;
  estimate_minutes?: number | null;
  department?: string | null;
  /** 0102 action-item category code (configurable value set). */
  category?: string | null;
  story_points?: number | null;
  due_date?: number | null;
  start_date?: number | null;
  /** 0024 Planner parity: repeat cadence + board visibility. */
  recurrence?: string | null;
  show_in_board?: number | null;
  origin_ticket_id?: string | null;
  rank?: string | null;
  /** 0036 Action Items: creator id + denormalized display name ("Requester").
   *  created_by_name is only present on enriched reads (getTask / listActionItems). */
  created_by?: string | null;
  created_by_name?: string | null;
  /** 0090 action-item sharing: can_edit===false => the viewer only has a
   *  read-only share (render read-only). undefined/true => editable. */
  can_edit?: boolean;
  /** 0090: true when this row reached the user via a read-only share.
   *  Informational only; `can_edit` is the authoritative gate. */
  shared?: boolean;
  created_at: number;
  updated_at: number;
}

/** 0090: a single read-only share of an action item. */
export interface ActionItemShare {
  user_id: string;
  name: string;
  shared_by: string | null;
}

/** GET/POST/DELETE /api/action-items/:id/shares → the full share roster. */
export interface ActionItemSharesResponse {
  shares: ActionItemShare[];
}

export interface BoardColumnRow {
  id: string;
  board_id: string;
  name: string;
  position: number;
  wip_limit?: number | null;
  color?: string | null;
  tasks: TaskRow[];
}

export interface BoardRow {
  id: string;
  project_id: string;
  name: string;
  created_at?: number;
}

/** GET /api/boards/:id → {board, columns:[{...,tasks}]}. */
export interface BoardResponse {
  board: BoardRow;
  columns: BoardColumnRow[];
}

export interface SavedViewRow {
  id: string;
  project_id: string;
  name: string;
  view_type: string;
  config_json?: string | null;
}

export interface ProjectsResponse {
  projects: ProjectRow[];
}
/** Board summary (no columns/tasks) returned with a project. */
export interface BoardSummary {
  id: string;
  project_id: string;
  name: string;
  kind: string;
}
export interface ProjectResponse {
  project: ProjectRow;
  views?: SavedViewRow[];
  boards?: BoardSummary[];
}

/** The 7 saved-view types the backend supports. */
export type ViewType = "list" | "board" | "timeline" | "calendar" | "table" | "workload" | "chart";

export interface CreateViewInput {
  name: string;
  view_type: ViewType;
  config?: Record<string, unknown>;
}
export interface ViewResponse {
  view: SavedViewRow;
}

/** GET /api/projects/:id/timeline → tasks + Gantt edges + milestones. */
export interface MilestoneRow {
  id: string;
  project_id: string;
  name: string;
  due_date?: number | null;
  reached_at?: number | null;
}
export interface DependencyRow {
  id: string;
  task_id: string;
  depends_on_id: string;
  kind: string;
}
export interface TimelineResponse {
  tasks: TaskRow[];
  dependencies: DependencyRow[];
  milestones: MilestoneRow[];
}

/** GET /api/projects/:id/calendar → tasks bucketed by due-date day (ISO yyyy-mm-dd). */
export interface CalendarResponse {
  from: number;
  to: number;
  days: Record<string, TaskRow[]>;
}

/** GET /api/projects/:id/table → tasks with their custom-field values. */
export interface TableResponse {
  fields: CustomFieldRow[];
  rows: (TaskRow & { fields: Record<string, unknown> })[];
}
export interface TasksResponse {
  tasks: TaskRow[];
}
export interface TaskResponse {
  task: TaskRow;
}

export interface CustomFieldValue {
  field_id: string;
  key?: string;
  label?: string;
  kind?: string;
  value: unknown;
}
/** A comment on a task (author_name joined server-side). */
/** Aggregated emoji reaction on a task comment (0034). */
export interface CommentReaction {
  emoji: string;
  count: number;
  mine: boolean;
}
export interface TaskCommentRow {
  id: string;
  ticket_id: string; // the task id (column aliased server-side for reuse)
  author_id: string | null;
  author_name?: string | null;
  body: string;
  created_at: number;
  /** Emoji reactions on this comment (0034); absent on legacy responses. */
  reactions?: CommentReaction[];
}

export interface TaskDetailResponse {
  task: TaskRow;
  fields: CustomFieldValue[];
  tags?: TagRow[];
  followers?: string[];
  comments?: TaskCommentRow[];
  subtasks?: TaskRow[];
  /** 0033 multi-assignee: full ordered set (position 0 = primary). */
  assignees?: { user_id: string; name: string }[];
  /** Files attached to the task / action item (parent_type 'task'). */
  attachments?: AttachmentRow[];
  /** 0081: true when the task's project has a linked Teams channel. */
  teams_linked?: boolean;
  /** 0082: approvals requested on this task / action item. */
  approvals?: TaskApprovalRow[];
  /** 0090 action-item sharing: the viewer's edit gate for THIS item.
   *  can_edit===false => render read-only. */
  can_edit?: boolean;
  /** 0090: full read-only-share roster. Present (possibly empty) ONLY when
   *  can_edit===true (managers see who it's shared with); omitted for read-only viewers. */
  shares?: ActionItemShare[];
}

export interface TaskCommentsResponse {
  comments: TaskCommentRow[];
}

/** Result of the task tag routes (GET/PUT/POST/DELETE /api/tasks/:id/tags). */
export interface TaskTagsResponse {
  tags: TagRow[];
}

export interface CreateProjectInput {
  key: string;
  name: string;
  description?: string;
  /** 'shared' (default) or 'private' (members + admins only). */
  visibility?: ProjectVisibility;
  // 0016 metadata:
  icon?: string;
  color?: string;
  status?: string;
  priority?: string;
  start_date?: number;
  end_date?: number;
  /** 0020 higher-ed: academic term code (see /api/config/terms). */
  term?: string;
}

/** PATCH /api/projects/:id — edit a project's own fields. All optional; a
 *  field set to null clears it (where the column is nullable). Dates are unix
 *  seconds. Mirrors WorkDb.updateProject. */
export interface UpdateProjectInput {
  name?: string;
  description?: string | null;
  priority?: string; // low|medium|high|urgent
  status?: string; // not_started|on_track|at_risk|delayed|on_hold|done
  icon?: string | null;
  color?: string | null;
  term?: string | null;
  lead_id?: string | null;
  start_date?: number | null;
  end_date?: number | null;
  /** 0081: Microsoft Teams Incoming Webhook URL ("" / null to unlink). */
  teams_webhook_url?: string | null;
  /** 0083: header/view customization JSON (compact, header note, sections). */
  ui_config?: string | null;
}

/** POST /api/projects/:id/rollover — clone a project into a new term. */
export interface RolloverProjectInput {
  key: string;
  name?: string;
  /** Target academic term code. */
  term?: string;
  /** Days to shift task start/due dates; omitted = derived from the terms. */
  shift_days?: number;
}

export interface CreateTaskInput {
  project_id: string;
  title: string;
  board_id?: string;
  column_id?: string;
  assignee_id?: string;
  /** Parent task id when creating a subtask. */
  parent_id?: string | null;
  /** 0017 built-in fields. priority_id references a ticket_priorities row. */
  priority_id?: string | null;
  estimate_minutes?: number | null;
  department?: string | null;
  /** 0024 Planner parity. */
  recurrence?: string;
  show_in_board?: number;
  /** Tag names to attach on create (replace set). */
  tags?: string[];
}
export interface UpdateTaskInput {
  title?: string;
  description?: string;
  status_code?: string;
  priority_code?: string;
  assignee_id?: string | null;
  /** 0033 multi-assignee: full ordered set; position 0 mirrors assignee_id. */
  assignee_ids?: string[];
  story_points?: number | null;
  due_date?: number | null;
  start_date?: number | null;
  /** 0017 built-in fields (validated when present; null clears). */
  priority_id?: string | null;
  estimate_minutes?: number | null;
  department?: string | null;
  category?: string | null;
  /** 0024 Planner parity: repeat cadence + board visibility. */
  recurrence?: string;
  show_in_board?: number;
}
export interface MoveTaskInput {
  column_id: string;
  after_task_id?: string | null;
}

// ---------------------------------------------------------------------------
// Agile (backlog / sprints / burndown / velocity)
// ---------------------------------------------------------------------------

export interface BacklogResponse {
  backlog: TaskRow[];
}
export interface SprintRow {
  id: string;
  board_id: string;
  name: string;
  goal: string | null;
  state: string;
  start_at: number | null;
  end_at: number | null;
}
export interface SprintsResponse {
  sprints: SprintRow[];
}
/** One day of GET /api/sprints/:id/burndown (mirrors work-db Burndown). */
export interface BurndownDay {
  date: string; // YYYY-MM-DD
  ideal: number;
  remaining: number;
}
export interface BurndownResponse {
  sprint_id: string;
  total_points: number;
  days: BurndownDay[];
}
/** One closed sprint of GET /api/boards/:id/velocity (work-db VelocityPoint). */
export interface VelocityRow {
  sprint_id: string;
  name: string;
  committed_points: number;
  completed_points: number;
}
export interface VelocityResponse {
  velocity: VelocityRow[];
}

// ---------------------------------------------------------------------------
// Project alt views (timeline / calendar / table / workload)
// ---------------------------------------------------------------------------

export interface WorkloadRow {
  assignee_id: string | null;
  assignee_name?: string | null;
  open_tasks: number;
  story_points?: number | null;
}
export interface WorkloadResponse {
  workload: WorkloadRow[];
}

// ---------------------------------------------------------------------------
// Dashboards
// ---------------------------------------------------------------------------

export interface DashboardRow {
  id: string;
  name: string;
  created_at: number;
}
export interface DashboardsResponse {
  dashboards: DashboardRow[];
}

/** A rendered widget. The worker's renderer returns a `kind` plus a data payload;
 *  we keep the data loosely typed and render number/bar/table shapes we recognize. */
export interface RenderedWidget {
  id: string;
  kind: string;
  title: string | null;
  layout_json?: string | null;
  // Renderer output — shape varies by widget kind.
  data?: unknown;
  value?: number | null;
  series?: { label: string; value: number }[];
  rows?: Record<string, unknown>[];
  columns?: string[];
  error?: string;
}
export interface DashboardRenderResponse {
  dashboard: DashboardRow;
  widgets: RenderedWidget[];
}

// ---------------------------------------------------------------------------
// My Work (GET /api/my-work?user=<id>)
// ---------------------------------------------------------------------------

export interface OpenTimerRow {
  id: string;
  task_id: string;
  task_title?: string | null;
  user_id: string;
  started_at: number;
}
export interface MyWorkResponse {
  /** Open PROJECT tasks assigned to the user (excludes Action Items). */
  tasks: TaskRow[];
  /** Open standalone Action Items the user created or is assigned to (0036). */
  action_items?: TaskRow[];
  tickets: TicketRow[];
  /** Tickets the user requested (is the client of) — by requester id or email. */
  requested_tickets?: TicketRow[];
  /** Open tickets assigned to the user's reporting subtree (manager view). */
  team_tickets?: TicketRow[];
  /** Open facility work orders assigned to the user. */
  work_orders?: MyWorkOrderRow[];
  /** Facility requests the user submitted. */
  facility_requests?: MyWorkOrderRow[];
  /** Open change requests the user owns / requested / is assigned / approves. */
  changes?: MyWorkChangeRow[];
  open_timers: OpenTimerRow[];
}

/** A change request trimmed for the My Work list. */
export interface MyWorkChangeRow {
  id: string;
  number: number;
  title: string;
  status: string;
  risk: string;
}

/** A facility work-order/request row trimmed for the My Work lists. */
export interface MyWorkOrderRow {
  id: string;
  number: number;
  title: string;
  status: string;
  priority: string;
}

// ---------------------------------------------------------------------------
// Action Items (standalone tasks; GET/POST /api/action-items)
// ---------------------------------------------------------------------------

/** Create payload for a standalone Action Item. No project_id — the server
 *  resolves the hidden container. `parent_id` creates a subtask. */
export interface CreateActionItemInput {
  title: string;
  description?: string | null;
  assignee_ids?: string[];
  priority_id?: string | null;
  estimate_minutes?: number | null;
  department?: string | null;
  category?: string | null;
  start_date?: number | null;
  due_date?: number | null;
  recurrence?: string;
  tags?: string[];
  parent_id?: string | null;
}
/** Action Items are tasks; the list returns the same TaskRow shape. */
export interface ActionItemsResponse {
  tasks: TaskRow[];
}

// ---------------------------------------------------------------------------
// Project access control (membership, visibility, access requests)
// ---------------------------------------------------------------------------
export type ProjectRole = "viewer" | "editor" | "manager";
export type ProjectVisibility = "shared" | "private";
export type AccessRequestStatus = "pending" | "approved" | "denied" | "cancelled";

export interface ProjectMember {
  project_id: string;
  user_id: string;
  role: ProjectRole;
  name: string;
  email: string;
  added_by: string | null;
  created_at: number;
  updated_at: number;
}
export interface ProjectMembersResponse {
  members: ProjectMember[];
}

export interface AccessRequest {
  id: string;
  project_id: string;
  user_id: string;
  requested_role: ProjectRole;
  status: AccessRequestStatus;
  note: string | null;
  decided_by: string | null;
  decided_at: number | null;
  created_at: number;
  updated_at: number;
}
export interface AccessRequestWithContext extends AccessRequest {
  user_name: string;
  user_email: string;
  project_name: string;
}
export interface AccessRequestResponse {
  request: AccessRequest;
}
export interface PendingAccessRequestsResponse {
  requests: AccessRequestWithContext[];
}
export interface MyAccessRequestsResponse {
  requests: AccessRequest[];
}

// ---------------------------------------------------------------------------
// Settings tabs (read-only loads for this slice)
// ---------------------------------------------------------------------------

export interface CustomFieldRow {
  id: string;
  entity_type: string;
  key: string;
  label: string;
  kind: string;
  config_json?: string | null;
  created_at?: number;
}
export interface CustomFieldsResponse {
  fields: CustomFieldRow[];
}

export interface AutomationRuleRow {
  id: string;
  name: string;
  entity_type: string;
  trigger_json?: string | null;
  enabled: number;
  created_at?: number;
}
export interface AutomationsResponse {
  rules: AutomationRuleRow[];
}

export interface FormRow {
  id: string;
  name: string;
  slug: string;
  target_type: string;
  /** What the form creates: 'ticket' | 'task' | 'work_order' (supersedes target_type). */
  target_kind?: string | null;
  target_project_id: string | null;
  /** Ticket forms: default status for created tickets (null → system default). */
  default_status_id?: string | null;
  /** Public-page presentation. */
  description?: string | null;
  logo_url?: string | null;
  /** When set, a notification is emailed here on every submission. */
  notify_email?: string | null;
  created_at?: number;
}
/** A recorded form submission (Forms → view submissions). */
export interface FormSubmissionRow {
  id: string;
  form_id: string;
  payload_json: string;
  created_entity_type: string | null;
  created_entity_id: string | null;
  submitted_by_contact: string | null;
  created_at: number;
}
export interface FormSubmissionsResponse {
  submissions: FormSubmissionRow[];
}
/** Conversion analytics for a form (#4). */
export interface FormStats {
  views: number;
  submissions: number;
  byEntity: { entity_type: string; n: number }[];
}
export interface FormStatsResponse {
  stats: FormStats;
}
export interface FormsResponse {
  forms: FormRow[];
}

export interface TemplateRow {
  id: string;
  key: string;
  name: string;
  description: string | null;
  kind?: string | null;
  created_at?: number;
}
export interface TemplatesResponse {
  templates: TemplateRow[];
}

export interface SavedFilterRow {
  id: string;
  name: string;
  query: string;
  entity_type: string;
  created_at?: number;
}
export interface FiltersResponse {
  filters: SavedFilterRow[];
}

// ---------------------------------------------------------------------------
// SSO (GET /api/auth/sso/connections, POST /api/auth/sso/:id/begin)
// ---------------------------------------------------------------------------

/** Public, non-secret view of an enabled SSO connection (login buttons). */
export interface SsoConnection {
  id: string;
  provider: string; // oidc | saml | google | microsoft
  display_name: string;
  email_domain: string | null;
}
export interface SsoConnectionsResponse {
  connections: SsoConnection[];
}
export interface SsoBeginResponse {
  authorizationUrl: string;
}

/** Admin view of an SSO sign-in connection (client secret never returned). */
export interface SsoConnectionAdminRow {
  id: string;
  provider: string;
  display_name: string;
  enabled: number;
  email_domain: string | null;
  jit_provisioning: number;
  default_role_id: string | null;
  enforce_sso: number;
  has_client_secret: boolean;
  config: Record<string, unknown>;
  created_at: number;
  updated_at: number;
}
export interface SsoConnectionsAdminResponse {
  connections: SsoConnectionAdminRow[];
}
/** Create/update an OIDC sign-in connection. provider 'microsoft' derives the
 *  Entra endpoints from directory_id; 'oidc' needs explicit endpoint URLs. */
export interface CreateSsoConnectionInput {
  provider: "microsoft" | "oidc";
  display_name?: string;
  client_id: string;
  client_secret?: string;
  /** Microsoft Entra: directory (tenant) id used to derive the endpoints. */
  directory_id?: string;
  /** Generic OIDC: explicit endpoints. */
  issuer?: string;
  authorization_endpoint?: string;
  token_endpoint?: string;
  jwks_uri?: string;
  userinfo_endpoint?: string;
  scopes?: string;
  email_domain?: string | null;
  jit_provisioning?: boolean;
  default_role_id?: string | null;
  enforce_sso?: boolean;
}

// ---------------------------------------------------------------------------
// Directory connections (Azure Entra ID → pull/provision users). Admin-only.
// The client secret is encrypted at rest and never returned by the API.
// ---------------------------------------------------------------------------

export interface DirectoryConnection {
  id: string;
  provider: string; // "entra"
  display_name: string;
  directory_id: string;
  client_id: string;
  group_id: string | null;
  jit_provisioning: number; // 0 | 1
  deactivate_removed: number; // 0 | 1
  enabled: number; // 0 | 1
  last_sync_at: number | null;
  last_sync_status: string | null;
}
export interface DirectoryConnectionsResponse {
  connections: DirectoryConnection[];
}
export interface CreateEntraConnectionInput {
  displayName: string;
  directoryId: string;
  clientId: string;
  clientSecret: string;
  groupId?: string;
}
export interface DirectorySyncSummary {
  status: string; // "ok" | "error" | ...
  users_seen?: number;
  users_created?: number;
  users_updated?: number;
  users_deactivated?: number;
  error?: string;
}
export interface DirectorySyncRun {
  id: string;
  connection_id: string;
  status: string;
  users_seen: number;
  users_created: number;
  users_updated: number;
  users_deactivated: number;
  error: string | null;
  started_at: number;
  finished_at: number | null;
}

// ---------------------------------------------------------------------------
// Notifications (GET /api/notifications?user=<id>, POST …/:id/read, …/read-all)
// ---------------------------------------------------------------------------

export interface NotificationRow {
  id: string;
  user_id: string;
  kind: string;
  title: string;
  body: string | null;
  link: string | null;
  read_at: number | null;
  created_at: number;
}
export interface NotificationsResponse {
  notifications: NotificationRow[];
  unread: number;
}

// ---------------------------------------------------------------------------
// Global search (GET /api/search?q=)
// ---------------------------------------------------------------------------

export interface SearchResult {
  type: "ticket" | "task" | "article" | "contact" | "work_order" | "asset";
  id: string;
  title: string;
  snippet?: string;
  url: string;
  updated_at?: number;
  /** Raw work-order number; formatted client-side with the facility prefix. */
  number?: number;
}
export interface SearchResponse {
  tickets: SearchResult[];
  tasks: SearchResult[];
  articles: SearchResult[];
  contacts: SearchResult[];
  workOrders: SearchResult[];
  assets: SearchResult[];
  total: number;
}

// ---------------------------------------------------------------------------
// Tenant config / customization (statuses / priorities / canned / hours / branding)
// ---------------------------------------------------------------------------

export interface TicketStatusRow {
  id: string;
  code: string;
  name: string;
  category: "open" | "pending" | "closed";
  position: number;
  is_default: number;
  color?: string | null;
}
/** One normalized ticket row sent to the bulk-import endpoint. Mirrors a subset
 *  of the create-ticket fields; the Settings importer maps CSV columns into this. */
export interface ImportTicketRow {
  subject: string;
  description?: string;
  requester_name?: string;
  requester_email?: string;
  priority_code?: string;
  status_code?: string;
  category_id?: string;
  department?: string;
  assignee_id?: string;
  due_date?: number; // unix seconds
  created_at?: number; // unix seconds — original creation time, preserved on import
}
export interface ImportTicketsResponse {
  created: number;
  failed: number;
  results: { index: number; ok: boolean; number?: number; error?: string }[];
}

export interface StatusesResponse {
  statuses: TicketStatusRow[];
}
export interface StatusResponse {
  status: TicketStatusRow;
}
export interface CreateStatusInput {
  code: string;
  name: string;
  category: "open" | "pending" | "closed";
  color?: string | null;
}

/** Higher-ed: one configurable academic term ("Fall 2026"); 0020. */
export interface AcademicTermRow {
  id: string;
  code: string; // e.g. 'fall-2026'
  name: string; // e.g. 'Fall 2026'
  starts_on: number | null; // unix seconds
  ends_on: number | null; // unix seconds
  is_current: number; // exactly one current
  position: number;
  created_at: number;
}
export interface TermsResponse {
  terms: AcademicTermRow[];
}
export interface TermResponse {
  term: AcademicTermRow;
}
export interface CreateTermInput {
  code: string;
  name: string;
  starts_on?: number | null;
  ends_on?: number | null;
  position?: number;
}

export interface TicketPriorityRow {
  id: string;
  code: string;
  name: string;
  position: number;
  is_default: number;
  /** Optional hex color for the priority pill (null = built-in per-code color). */
  color?: string | null;
}
export interface PrioritiesResponse {
  priorities: TicketPriorityRow[];
}
export interface PriorityResponse {
  priority: TicketPriorityRow;
}

/** Action-item / project-task priorities (0108) — separate set from tickets. */
export type TaskPriorityRow = TicketPriorityRow;
export interface TaskPrioritiesResponse {
  priorities: TaskPriorityRow[];
}
export interface TaskPriorityResponse {
  priority: TaskPriorityRow;
}
export interface CreatePriorityInput {
  code: string;
  name: string;
  color?: string | null;
}

/** Configurable ticket types (GET/POST /api/config/ticket-types). */
export interface TicketTypeRow {
  id: string;
  code: string;
  name: string;
  position: number;
  is_default: number;
  created_at: number;
  /** Optional hex color for the type chip + Settings swatch (null = default). */
  color: string | null;
}
export interface TicketTypesResponse {
  ticket_types: TicketTypeRow[];
}
export interface TicketTypeResponse {
  ticket_type: TicketTypeRow;
}

/** Configurable task / action-item status (0061). tasks.status stores the code. */
export interface TaskStatusRow {
  id: string;
  code: string;
  name: string;
  position: number;
  is_default: number;
  created_at: number;
  color?: string | null;
}
export interface TaskStatusesResponse {
  task_statuses: TaskStatusRow[];
}
export interface TaskStatusResponse {
  task_status: TaskStatusRow;
}

/** Config canned responses (GET/POST /api/config/canned). */
export interface ConfigCannedRow {
  id: string;
  title: string;
  body: string;
  created_at: number;
}
export interface ConfigCannedResponse {
  canned: ConfigCannedRow[];
}
export interface ConfigCannedCreateResponse {
  canned: ConfigCannedRow;
}
export interface CreateCannedInput {
  title: string;
  body: string;
}

/** One weekday's business hours; null open/close means the day is closed. */
export interface BusinessHoursRow {
  weekday: number; // 0=Sun .. 6=Sat
  open_min: number | null;
  close_min: number | null;
}
export interface BusinessHoursResponse {
  hours: BusinessHoursRow[];
}
export interface SetBusinessHoursInput {
  weekday: number;
  open_min: number | null;
  close_min: number | null;
}

export interface Branding {
  primary_color: string | null;
  logo_url: string | null;
  secondary_color: string | null;
  company_name: string | null;
  support_email: string | null;
  favicon_url: string | null;
}
export interface BrandingResponse {
  branding: Branding;
}
export interface SetBrandingInput {
  primary_color?: string | null;
  logo_url?: string | null;
  secondary_color?: string | null;
  company_name?: string | null;
  support_email?: string | null;
  favicon_url?: string | null;
}

/** Workspace experience preferences (Settings → General). Module visibility is
 *  NOT here — it is governed per role (Settings → Roles). */
export interface WorkspaceConfig {
  name: string | null;
  landing: string | null;
  /** Maintenance mode: non-admins see a message across all modules when on. */
  maintenance_enabled: boolean;
  maintenance_message: string | null;
}
export interface WorkspaceConfigResponse {
  config: WorkspaceConfig;
}
export interface SetWorkspaceConfigInput {
  name?: string | null;
  landing?: string | null;
  maintenance_enabled?: boolean;
  maintenance_message?: string | null;
}

/** AI email-intake preferences (Settings → Email). When enabled, the inbound
 *  email worker asks Claude to extract structured fields and pre-fills the
 *  resulting ticket; default_category/default_priority are CODE fallbacks used
 *  when the model is unsure. */
/** 0097: an AI-suggested possible duplicate of a ticket (joined to the candidate). */
export interface TicketDuplicate {
  id: string;
  ticket_id: string;
  duplicate_ticket_id: string;
  reason: string;
  score: number;
  status: "suggested" | "dismissed" | "confirmed";
  source: "on_demand" | "intake";
  created_by: string | null;
  created_at: number;
  updated_at: number;
  number: number;
  subject: string;
  status_id: string;
  priority_id: string;
}
export interface TicketDuplicatesResponse {
  duplicates: TicketDuplicate[];
}

export interface EmailIntakeConfig {
  enabled: boolean;
  default_category: string | null;
  default_priority: string | null;
  /** Ticket type CODE applied to intake tickets (null = the tenant default). */
  default_ticket_type: string | null;
  /** Ticket status code applied to intake tickets; null = the tenant default lane. */
  default_status: string | null;
  /** Poll the connected inbox on a schedule instead of relying only on pushed mail. */
  check_enabled: boolean;
  /** AI duplicate-detection: when on, new tickets are checked against recent
   *  tickets for likely duplicates (on intake and on-demand). Default false. */
  dedupe_enabled: boolean;
  /** Thread inbound email replies onto their ticket as comments (matched by the
   *  ticket number in the subject). Default true. */
  thread_replies: boolean;
  /** TEST redirect: when set, ALL outbound email goes to this one address. Off = null. */
  test_redirect_to: string | null;
  /** How often (minutes) to check the inbox when check_enabled. */
  check_interval_minutes: number;
  /** Inbox connection (for polling). Password never returned — see has_inbox_password. */
  inbox_host: string | null;
  inbox_port: number | null;
  inbox_ssl: boolean;
  inbox_username: string | null;
  has_inbox_password: boolean;
  /** READ-ONLY: unix-sec of the last scheduled inbox check (0 = never). Not user-settable. */
  last_check?: number;
  /** READ-ONLY: structured summary of the most recent scheduled poll run, or null. */
  last_run?: EmailIntakeRun | null;
}
/** Observability summary of one scheduled AI-email-intake poll run (read-only). */
export interface EmailIntakeRun {
  ran_at: number;
  trigger: "poll" | "push";
  total: number;
  processed: number;
  created: number;
  errors: number;
  ai_used: number;
  last_error: string | null;
}
export interface EmailIntakeConfigResponse {
  config: EmailIntakeConfig;
}
export interface SetEmailIntakeConfigInput {
  enabled?: boolean;
  default_category?: string | null;
  default_priority?: string | null;
  default_ticket_type?: string | null;
  default_status?: string | null;
  check_enabled?: boolean;
  dedupe_enabled?: boolean;
  thread_replies?: boolean;
  test_redirect_to?: string | null;
  check_interval_minutes?: number;
  inbox_host?: string | null;
  inbox_port?: number | null;
  inbox_ssl?: boolean;
  inbox_username?: string | null;
  /** Write-only: set the IMAP password ("" clears it). */
  inbox_password?: string | null;
}
/** Landing routes a tenant may choose as the post-login home (mirrors the data
 *  layer's WORKSPACE_LANDING_ROUTES). `label` drives the Settings picker. */
export const WORKSPACE_LANDING_OPTIONS: { value: string; label: string }[] = [
  { value: "/tickets", label: "Tickets" },
  { value: "/my-work", label: "My Work" },
  { value: "/action-items", label: "Action Items" },
  { value: "/projects", label: "Projects" },
  { value: "/dashboards", label: "Dashboards" },
  { value: "/reports", label: "Reports" },
  { value: "/kb", label: "Knowledge Base" },
];

// ---------------------------------------------------------------------------
// SLA (policies + per-ticket timers)
// ---------------------------------------------------------------------------

export interface SlaPolicyRow {
  id: string;
  name: string;
  priority_id: string | null;
  first_response_mins: number;
  resolution_mins: number;
  active: number;
  created_at: number;
}
export interface SlaPoliciesResponse {
  policies: SlaPolicyRow[];
}
export interface SlaPolicyResponse {
  policy: SlaPolicyRow;
}
export interface CreateSlaPolicyInput {
  name: string;
  priority_id?: string;
  first_response_mins: number;
  resolution_mins: number;
}

export interface SlaTimerRow {
  id: string;
  ticket_id: string;
  policy_id: string | null;
  kind: string; // 'first_response' | 'resolution'
  due_at: number;
  satisfied_at: number | null;
  breached: number;
  created_at: number;
}
export interface SlaTimersResponse {
  timers: SlaTimerRow[];
}

// ---------------------------------------------------------------------------
// Reports (GET /api/reports, GET /api/reports/:id/run)
// ---------------------------------------------------------------------------

export interface ReportRow {
  id: string;
  name: string;
  description: string | null;
  module: string; // 'helpdesk' | 'work' | 'combined'
  kind: string;
  config_json: string;
  owner_id: string | null;
  is_shared: number;
  created_at: number;
  updated_at: number;
}
export interface ReportsResponse {
  reports: ReportRow[];
}
/** A scheduled email delivery of a report. `cron` is a frequency token. */
export interface ReportSchedule {
  id: string;
  report_id: string;
  cron: string; // 'daily' | 'weekly' | 'monthly'
  recipients_json: string;
  last_run_at: number | null;
  enabled: number;
  created_at: number;
}
export interface ReportSchedulesResponse {
  schedules: ReportSchedule[];
}
/** Uniform tabular result of a report run. */
export interface ReportResult {
  kind: string;
  columns: string[];
  rows: Array<Record<string, string | number | null>>;
  /** Optional rendering hints from the engine: suggested chart type, the
   *  category (x/label) column, and either one value column or a multi-series set. */
  meta?: {
    chart?: "bar" | "line" | "pie";
    category?: string;
    value?: string;
    series?: string[];
    interval?: string;
    [k: string]: unknown;
  };
}

// ---------------------------------------------------------------------------
// Audit log (GET /api/audit?entity_type=&entity_id=)
// ---------------------------------------------------------------------------

export interface AuditRow {
  id: string;
  actor_id: string | null;
  action: string;
  entity_type: string;
  entity_id: string;
  before_json: string | null;
  after_json: string | null;
  created_at: number;
}
export interface AuditResponse {
  audit: AuditRow[];
}

// ---------------------------------------------------------------------------
// Dashboard creation (POST /api/dashboards, POST /api/dashboards/:id/widgets)
// ---------------------------------------------------------------------------

/** Widget kinds accepted by the widgets route (mirrors dashboards-db WidgetKind). */
export type DashboardWidgetKind =
  | "number"
  | "bar"
  | "line"
  | "pie"
  | "timeline"
  | "workload"
  | "table";

export interface CreateDashboardResponse {
  dashboard: DashboardRow;
}

/** Body for POST /api/dashboards/:id/widgets. `query_json` is a WidgetQuerySpec
 *  object (see packages/ac-tenant-db dashboards-db): { source: 'tasks'|'tickets',
 *  metric: 'count'|'sum_points', groupBy?: 'status'|'assignee_id'|'project_id',
 *  filters?: { project_id?, status? } }. */
export interface CreateDashboardWidgetInput {
  kind: DashboardWidgetKind;
  title?: string;
  query_json?: unknown;
  layout_json?: unknown;
}

/** Stored widget row as returned by the create-widget route (no rendered data). */
export interface DashboardWidgetRow {
  id: string;
  dashboard_id: string;
  kind: string;
  title: string | null;
  query_json: string;
  layout_json: string;
  position: number;
}
export interface CreateDashboardWidgetResponse {
  widget: DashboardWidgetRow;
}

// ---------------------------------------------------------------------------
// Departments (higher-ed; 0021) + teams (the routing targets)
// ---------------------------------------------------------------------------

/** A team (group), as returned by GET /api/teams. */
export interface TeamRow {
  id: string;
  name: string;
  created_at: number;
}
export interface TeamsResponse {
  teams: TeamRow[];
}

export type TeamRole = "member" | "manager";
/** One of a user's team memberships (GET /api/users/:id/teams). */
export interface UserTeamRow {
  team_id: string;
  name: string;
  role: TeamRole;
}
export interface UserTeamsResponse {
  teams: UserTeamRow[];
}

/** Higher-ed: one configurable department workspace ("Admissions"); 0021. */
export interface DepartmentRow {
  id: string;
  code: string; // e.g. 'admissions' — stored on tickets.department
  name: string; // e.g. 'Admissions'
  team_id: string | null; // ticket routing target (null = no routing)
  position: number;
  change_management: number; // 0/1 — Change-Mgmt approvers + selectable on changes (0099)
  /** 0110: JSON [{min_approvals,field,value}] auto field-update rules; null = none. */
  approval_rules?: string | null;
  created_at: number;
}
export interface DepartmentsResponse {
  departments: DepartmentRow[];
}
export interface DepartmentResponse {
  department: DepartmentRow;
}
export interface CreateDepartmentInput {
  code: string;
  name: string;
  team_id?: string | null;
  position?: number;
  change_management?: boolean;
  /** JSON string of approval-automation rules (0110). */
  approval_rules?: string | null;
}

/** A Change-Management field an approval rule can auto-update (0110). Each maps
 *  to a change_requests column + a configurable value set in the change config. */
export type ChangeAutoField = "status" | "risk" | "change_type" | "urgency" | "business_impact";

/** One department approval-automation rule (0110): when >= min_approvals
 *  approvers approve, set the change request's `field` to `value`. */
export interface DepartmentApprovalRule {
  min_approvals: number;
  field: ChangeAutoField;
  value: string;
}
/** A department's default Change-Management approver (0099). */
export interface DepartmentApprover {
  user_id: string;
  name: string | null;
}
export interface DepartmentApproversResponse {
  approvers: DepartmentApprover[];
}

// ---------------------------------------------------------------------------
// Sprint mutations (POST /api/boards/:id/sprints, /api/sprints/:id/{start,complete})
// ---------------------------------------------------------------------------

export interface CreateSprintInput {
  name: string;
  goal?: string;
  start_at?: number;
  end_at?: number;
}
export interface SprintResponse {
  sprint: SprintRow;
}
/** POST /api/sprints/:id/complete → count of unfinished tasks sent back to the backlog. */
export interface CompleteSprintResponse {
  moved: number;
}

// ---------------------------------------------------------------------------
// Goals / OKRs + Portfolios (GET/POST /api/goals, /api/portfolios, …)
// ---------------------------------------------------------------------------

export type GoalStatus = "on_track" | "at_risk" | "off_track" | "done";

/** A goal row (goals-db GoalRow). Hierarchy via parent_id; progress is 0..1. */
export interface GoalRow {
  id: string;
  parent_id: string | null;
  name: string;
  description: string | null;
  owner_id: string | null;
  status: string; // on_track|at_risk|off_track|done
  progress: number; // 0..1
  start_date: number | null;
  due_date: number | null;
  created_at: number;
  updated_at: number;
}
export interface GoalsResponse {
  goals: GoalRow[];
}
export interface GoalResponse {
  goal: GoalRow;
}
/** POST /api/goals body. NOTE: the create route only reads name/description/
 *  parent_id — dates and status are set via PATCH /api/goals/:id afterwards. */
export interface CreateGoalInput {
  name: string;
  description?: string;
  parent_id?: string;
}
export interface UpdateGoalInput {
  name?: string;
  description?: string | null;
  owner_id?: string | null;
  status?: GoalStatus;
  parent_id?: string | null;
  start_date?: number | null;
  due_date?: number | null;
}
/** POST /api/goals/:id/rollup → recomputed progress (0..1). */
export interface GoalRollupResponse {
  progress: number;
}

export interface PortfolioRow {
  id: string;
  name: string;
  description: string | null;
  created_at: number;
}
export interface PortfoliosResponse {
  portfolios: PortfolioRow[];
}
export interface PortfolioResponse {
  portfolio: PortfolioRow;
}

// ---------------------------------------------------------------------------
// Forms builder (intake admin) — full shapes for the /forms page
// ---------------------------------------------------------------------------

/** Field kinds supported by forms-db (packages/ac-tenant-db/src/forms-db.ts). */
export type FormFieldKind = "text" | "textarea" | "select" | "checkbox" | "date" | "number" | "email";

/** Full form row as the edge actually returns it (FormRow above is the slim view). */
export interface FormDetailRow extends FormRow {
  is_published?: number;
  portal_enabled?: number;
  updated_at?: number;
}

export interface FormFieldRow {
  id: string;
  form_id: string;
  /** Target entity attribute this answer sets (null = unmapped/summary only). */
  maps_to?: string | null;
  key: string;
  label: string;
  kind: string; // FormFieldKind
  required: number;
  options_json: string | null;
  branching_json: string | null;
  position: number;
}

/** Branching rule: the field is shown only when payload[field] equals the value. */
export interface FormBranchingRule {
  showIf: { field: string; equals: string | number | boolean };
}

/** Body for POST /api/forms. NOTE: the edge route does not accept
 *  portal_enabled, and no publish route is exposed — new forms start
 *  unpublished + internal-only. */
export type FormTargetKind = "ticket" | "task" | "work_order";
export interface CreateFormInput {
  name: string;
  slug: string;
  target_kind: FormTargetKind;
  target_project_id?: string | null;
  portal_enabled?: boolean;
  default_status_id?: string | null;
  description?: string | null;
  logo_url?: string | null;
  notify_email?: string | null;
}
/** PATCH /api/forms/:id — partial form config. */
export interface UpdateFormInput {
  name?: string;
  slug?: string;
  target_kind?: FormTargetKind;
  target_project_id?: string | null;
  portal_enabled?: boolean;
  default_status_id?: string | null;
  description?: string | null;
  logo_url?: string | null;
  notify_email?: string | null;
}
export interface CreateFormResponse {
  form: FormDetailRow;
}
/** GET /api/forms/:slug — the form plus its ordered fields. */
export interface FormWithFieldsResponse {
  form: FormDetailRow;
  fields: FormFieldRow[];
}
/** Body for POST /api/forms/:id/fields. */
export interface AddFormFieldInput {
  key: string;
  label: string;
  kind: FormFieldKind;
  required?: boolean;
  options?: string[];
  branching?: FormBranchingRule;
  maps_to?: string | null;
}
/** PATCH a field: partial; options/branching may be null to CLEAR the column. */
export interface UpdateFormFieldInput {
  key?: string;
  label?: string;
  kind?: FormFieldKind;
  required?: boolean;
  options?: string[] | null;
  branching?: FormBranchingRule | null;
  position?: number;
  maps_to?: string | null;
}
export interface AddFormFieldResponse {
  field: FormFieldRow;
}

// ---------------------------------------------------------------------------
// Automations builder — decoded rule shapes for the /automations page
// ---------------------------------------------------------------------------

/** Operators understood by the engine (automations-db compare()). */
export type AutomationConditionOp = "=" | "!=" | ">" | "<" | ">=" | "<=" | "~";
export interface AutomationCondition {
  field: string;
  op: AutomationConditionOp;
  value: string | number;
}
export interface AutomationAction {
  type: string;
  params: Record<string, unknown>;
}
/** Full rule as the edge returns it: raw row plus decoded trigger/conditions/actions. */
export interface AutomationRuleDetail extends AutomationRuleRow {
  conditions_json?: string | null;
  actions_json?: string | null;
  trigger?: { event: string };
  conditions?: AutomationCondition[];
  actions?: AutomationAction[];
  created_by?: string | null;
  updated_at?: number;
}
export interface AutomationRulesResponse {
  rules: AutomationRuleDetail[];
}
/** Body for POST /api/automations. */
export interface CreateAutomationInput {
  name: string;
  entity_type: "ticket" | "task";
  trigger: { event: string };
  conditions?: AutomationCondition[];
  actions?: AutomationAction[];
}
/** Body for PATCH /api/automations/:id — every field optional (partial update). */
export interface UpdateAutomationInput {
  name?: string;
  trigger?: { event: string };
  conditions?: AutomationCondition[];
  actions?: AutomationAction[];
  enabled?: boolean;
}
export interface AutomationRuleResponse {
  rule: AutomationRuleDetail;
}

// ---------------------------------------------------------------------------
// Knowledge base (internal, team-shared) — /kb page. Mirrors kb_categories/
// kb_articles (migration 0002) + the /api/kb/* routes. Published articles are
// shared with everyone in the tenant; drafts stay visible to their owner (+admins).
// Published articles also power ticket deflection (suggest on ticket filing).
// ---------------------------------------------------------------------------

export interface KbCategoryRow {
  id: string;
  name: string;
  slug: string;
  position: number;
  created_at: number;
}

export type KbArticleStatus = "draft" | "pending_review" | "published";

export interface KbArticleRow {
  id: string;
  category_id: string | null;
  title: string;
  slug: string;
  body_md: string;
  status: KbArticleStatus;
  author_id: string | null;
  created_at: number;
  updated_at: number;
}

/** GET /api/kb/articles items: article + its (optional) category + owner name. */
export interface KbArticleListItem extends KbArticleRow {
  category_name: string | null;
  category_slug: string | null;
  /** Display name of the creator/owner (author_id → users.name); null if gone. */
  author_name: string | null;
  /** Number of tickets linked to this article (0095); folded into the list read. */
  linked_ticket_count?: number;
}

export interface KbArticlesResponse {
  articles: KbArticleListItem[];
}
export interface KbArticleResponse {
  article: KbArticleRow;
}
export interface KbCategoriesResponse {
  categories: KbCategoryRow[];
}
export interface KbCategoryResponse {
  category: KbCategoryRow;
}
/** Body for POST /api/kb/articles (server sets status='draft' + author). */
export interface CreateKbArticleInput {
  title: string;
  slug: string;
  body_md: string;
  category_id?: string | null;
}
/** Body for PATCH /api/kb/articles/:id. */
export interface UpdateKbArticleInput {
  title?: string;
  slug?: string;
  body_md?: string;
  category_id?: string | null;
}
/** GET /api/kb/suggest?q=… → up to 5 published matches (ticket deflection). */
export interface KbSuggestResponse {
  articles: KbArticleRow[];
}

// --- KB <-> ticket two-way links (0095) ---
/** A ticket summary linked to an article (article -> tickets direction). */
export interface KbLinkedTicket {
  ticket_id: string;
  number: number;
  subject: string;
  status: string | null;
  priority_code: string | null;
  created_at: number;
  linked_at: number;
}
export interface KbLinkedTicketsResponse {
  tickets: KbLinkedTicket[];
}
/** An article linked to a ticket (ticket -> articles direction). */
export interface TicketLinkedArticle extends KbArticleListItem {
  linked_at: number;
}
export interface TicketKbArticlesResponse {
  articles: TicketLinkedArticle[];
}
/** Optional overrides for POST /api/tickets/:id/kb-articles/from-ticket. */
export interface CreateKbFromTicketInput {
  category_id?: string | null;
  body_md?: string;
}
/** Return of the from-ticket create: the new article + the ticket's linked list. */
export interface CreateKbFromTicketResponse {
  article: KbArticleRow;
  articles: TicketLinkedArticle[];
}

/** One turn in the KB chat assistant conversation. */
export interface KbChatMessage {
  role: "user" | "assistant";
  content: string;
}
/** An article the assistant drew its answer from (cited as [n] in the answer). */
export interface KbChatSource {
  id: string;
  title: string;
  slug: string;
}
/** POST /api/kb/chat → grounded answer + the articles it used + the saved chat id. */
export interface KbChatResponse {
  answer: string;
  sources: KbChatSource[];
  /** The chat this turn was saved to (0042); reuse it on the next turn. */
  chat_id?: string;
}
/** A saved KB chat session (0042), private to its owner. */
export interface KbChatSummary {
  id: string;
  title: string;
  created_at: number;
  updated_at: number;
}
export interface KbChatsResponse {
  chats: KbChatSummary[];
}
/** A persisted chat turn (assistant turns carry their sources). */
export interface KbChatStoredMessage extends KbChatMessage {
  sources?: KbChatSource[];
  created_at: number;
}
export interface KbChatDetailResponse {
  chat: KbChatSummary;
  messages: KbChatStoredMessage[];
}

// ---------------------------------------------------------------------------
// Legacy higher-ed helpdesk parity (ticket type / categories / locations /
// scheduling / checklist / approvals / time-spent). APPEND-ONLY: the blocks
// below use same-module interface MERGING to extend the ticket shapes above
// without touching them.
// ---------------------------------------------------------------------------

/** One checklist (to-do) item on a ticket. `done` is 0/1 from D1. */
export interface TicketChecklistItem {
  id: string;
  label: string;
  done: number;
  position: number;
}

export type TicketApprovalStatus = "pending" | "approved" | "denied";

/** One approval request on a ticket. */
export interface TicketApprovalRow {
  id: string;
  approver_id: string;
  approver_name: string | null;
  status: TicketApprovalStatus;
  note: string | null;
  requested_by: string | null;
  requested_by_name: string | null;
  created_at: number;
  decided_at: number | null;
}

/** Configurable campus location (GET/POST/PATCH/DELETE /api/config/locations). */
export interface LocationRow {
  id: string;
  name: string;
  position: number;
}
export interface LocationsResponse {
  locations: LocationRow[];
}
export interface LocationResponse {
  location: LocationRow;
}
export interface CreateLocationInput {
  name: string;
  position?: number;
}

// ===== Assets / inventory (0036) ==========================================

/** Asset status is now a configurable code (Settings ▸ Asset Statuses). */
export type AssetStatus = string;

/** Configurable asset category (Settings ▸ Asset types). */
export interface AssetTypeRow {
  id: string;
  name: string;
  position: number;
  created_at: number;
}
export interface AssetTypesResponse {
  asset_types: AssetTypeRow[];
}

/** Configurable asset CATEGORY (Settings ▸ Asset Categories; 0092) — a grouping
 *  dimension distinct from the asset "Type" lookup. */
export interface AssetCategoryRow {
  id: string;
  name: string;
  position: number;
  created_at: number;
}
export interface AssetCategoriesResponse {
  asset_categories: AssetCategoryRow[];
}
export interface AssetCategoryResponse {
  asset_category: AssetCategoryRow;
}

/** A configurable asset status (Settings ▸ Asset Statuses). */
export interface AssetStatusRow {
  id: string;
  code: string;
  name: string;
  color: string | null;
  position: number;
  is_system: number;
  created_at: number;
}
export interface AssetStatusesResponse {
  asset_statuses: AssetStatusRow[];
}
export interface AssetStatusResponse {
  asset_status: AssetStatusRow;
}

/** An inventory item joined with its type/holder/location display names. */
export interface AssetRow {
  id: string;
  asset_tag: string | null;
  name: string;
  type_id: string | null;
  type_name: string | null;
  category_id: string | null;
  category_name: string | null;
  serial: string | null;
  status: AssetStatus;
  holder_id: string | null;
  holder_name: string | null;
  location_id: string | null;
  location_name: string | null;
  notes: string | null;
  /** On-hand count (1 for individually-tracked items, higher for bulk inventory). */
  quantity: number;
  /** Units currently OUT — sum of quantities across open check-out rows. */
  checked_out: number;
  /** Derived: quantity − checked_out, floored at 0. Units free to check out. */
  available: number;
  /** Current open check-out period's due date (unix sec), null when none. */
  due_at: number | null;
  /** True when the current open check-out period is past its due date (derived). */
  is_overdue: boolean;
  created_at: number;
  updated_at: number;
}

/** One currently-open check-out on an asset (a holder + how many units). */
export interface AssetHolder {
  assignment_id: string;
  user_id: string | null;
  user_name: string | null;
  quantity: number;
  note: string | null;
  due_at: number | null;
  reminder_at: number | null;
  is_overdue: boolean;
  created_at: number;
}
export interface AssetsResponse {
  assets: AssetRow[];
}
export interface AssetResponse {
  asset: AssetRow;
}

/** One custody-log entry (check-out / check-in). */
export interface AssetAssignmentRow {
  id: string;
  asset_id: string;
  user_id: string | null;
  user_name: string | null;
  action: "checkout" | "checkin";
  by_user_id: string | null;
  by_user_name: string | null;
  note: string | null;
  due_at: number | null;
  reminder_at: number | null;
  status: "checked_out" | "returned";
  /** Derived: status='checked_out' AND due_at<now. */
  is_overdue: boolean;
  created_at: number;
}

/** Current open check-out period summary (asset detail Dates & Reminder tab). */
export interface CheckoutPeriod {
  due_at: number | null;
  reminder_at: number | null;
  status: "checked_out" | "returned";
  is_overdue: boolean;
}

/** One custody PERIOD for the roster-wide history — a holder's tenure with an
 *  asset, from check-out (start_at) to the next event (end_at; null = still held). */
export interface AssetAssignmentPeriodRow {
  id: string;
  asset_id: string;
  asset_name: string;
  asset_tag: string | null;
  type_name: string | null;
  user_id: string | null;
  user_name: string | null;
  by_user_id: string | null;
  by_user_name: string | null;
  note: string | null;
  start_at: number;
  end_at: number | null;
}
export interface AssignmentHistoryResponse {
  history: AssetAssignmentPeriodRow[];
}

/** A ticket referencing an asset (asset detail "Related tickets" panel). */
export interface AssetTicketRef {
  id: string;
  number: number;
  subject: string;
  status_id: string;
  created_at: number;
}

/** GET/POST/DELETE /api/tickets/:id/assets → assets linked to a ticket. */
export interface TicketAssetsResponse {
  assets: AssetRow[];
}

/** GET /api/assets/:id/tickets → tickets linked to an asset. */
export interface AssetTicketsResponse {
  tickets: AssetTicketRef[];
}

/** GET /api/assets/:id → asset + custody log + related tickets. */
export interface AssetDetailResponse {
  asset: AssetRow;
  assignments: AssetAssignmentRow[];
  tickets: AssetTicketRef[];
  /** The current open check-out period, or null when not checked out. */
  current_period: CheckoutPeriod | null;
  /** Currently-open check-outs (who holds how many). Empty when fully available. */
  holders: AssetHolder[];
}

/** POST /api/assets/:id/checkout|checkin → refreshed asset + open holders. */
export interface AssetHoldersResponse {
  asset: AssetRow;
  holders: AssetHolder[];
}

export interface CreateAssetInput {
  name: string;
  asset_tag?: string | null;
  type_id?: string | null;
  category_id?: string | null;
  serial?: string | null;
  status?: AssetStatus;
  holder_id?: string | null;
  location_id?: string | null;
  notes?: string | null;
  quantity?: number | null;
}
export interface UpdateAssetInput {
  name?: string;
  asset_tag?: string | null;
  type_id?: string | null;
  category_id?: string | null;
  serial?: string | null;
  status?: AssetStatus;
  location_id?: string | null;
  notes?: string | null;
  quantity?: number;
}

/** One row of a bulk CSV import (type/holder/location by name/email). */
export interface AssetImportRow {
  name?: string;
  asset_tag?: string;
  type?: string;
  serial?: string;
  status?: string;
  holder?: string;
  location?: string;
  notes?: string;
  quantity?: string;
}
export interface AssetImportResult {
  created: number;
  errors: { line: number; message: string }[];
}

/** Hierarchical ticket category (Category → Sub Type → Item). */
export interface TicketCategoryRow {
  id: string;
  name: string;
  parent_id: string | null;
  position: number;
  /** 0089: user auto-assigned onto a ticket when this category is chosen. */
  default_assignee_id?: string | null;
}
export interface TicketCategoriesResponse {
  categories: TicketCategoryRow[];
}

/** Merge: extra detail fields returned by GET /api/tickets/:id. */
export interface TicketDetail {
  created_by?: string | null;
  created_by_name?: string | null;
  /** service_request | incident | problem (legacy imports may carry others). */
  type?: string | null;
  category_id?: string | null;
  room?: string | null;
  scheduled_from?: number | null;
  scheduled_to?: number | null;
  requester_phone?: string | null;
  /** staff | faculty | student | alumni | other (lowercase). */
  requester_affiliation?: string | null;
  requester_location?: string | null;
  /** 0074: campuses-list code (top of the shared place cascade). */
  campus?: string | null;
  /** 0073: buildings-list code (child of campus). Room is the existing `room`. */
  building?: string | null;
  escalation_level?: number | null;
  /** Tech-group routing target. NOTE: not in the written PATCH contract —
   *  sent optimistically; the worker may ignore it (flagged to backend). */
  team_id?: string | null;
  checklist?: TicketChecklistItem[];
  approvals?: TicketApprovalRow[];
}

/** Merge: extra PATCH /api/tickets/:id fields (all nullable; unix seconds). */
export interface UpdateTicketInput {
  type?: string | null;
  source?: string | null;
  room?: string | null;
  scheduled_from?: number | null;
  scheduled_to?: number | null;
  category_id?: string | null;
  requester_phone?: string | null;
  requester_affiliation?: string | null;
  requester_location?: string | null;
  /** 0074/0073: shared place cascade — campus + building codes; room is `room`. */
  campus?: string | null;
  building?: string | null;
  escalation_level?: number | null;
  /** See TicketDetail.team_id note — beyond the written PATCH contract. */
  team_id?: string | null;
}

/** Merge: extra create-intake fields (mirror of the PATCH additions). */
export interface CreateTicketInput {
  type?: string;
  source?: string;
  requester_phone?: string;
  requester_affiliation?: string;
  requester_location?: string;
  /** 0074/0073: shared place cascade — campus + building codes; room is `room`. */
  campus?: string;
  building?: string;
  room?: string;
}

/** Merge: agents can log minutes with a comment. */
export interface AddCommentInput {
  time_spent_mins?: number;
}
export interface TicketCommentRow {
  time_spent_mins?: number | null;
}

/** POST /api/tickets/:id/checklist + PATCH /api/checklist/:id responses. */
export interface ChecklistItemResponse {
  item?: TicketChecklistItem;
  ok?: boolean;
}
/** POST /api/tickets/:id/approvals + POST /api/approvals/:id/decide responses. */
export interface ApprovalResponse {
  approval?: TicketApprovalRow;
  ok?: boolean;
}
export interface RequestApprovalInput {
  approver_id: string;
  note?: string;
}
export interface DecideApprovalInput {
  approve: boolean;
  note?: string;
}

/** A pending ticket approval awaiting the current user (Access Requests page). */
export interface PendingTicketApproval {
  id: string;
  ticket_id: string;
  ticket_number: number | null;
  ticket_subject: string;
  note: string | null;
  requested_by_name: string | null;
  created_at: number;
}
/** A pending TASK/action-item approval awaiting the current user. */
export interface PendingTaskApproval {
  id: string;
  task_id: string;
  task_title: string;
  note: string | null;
  requested_by_name: string | null;
  created_at: number;
}
/** A pending KB publish approval awaiting the current user (0096). */
export interface PendingKbApproval {
  id: string;
  article_id: string;
  article_title: string;
  note: string | null;
  requested_by_name: string | null;
  created_at: number;
}
/** A pending change-request approval awaiting the current user (0098). */
export interface PendingChangeApproval {
  id: string;
  request_id: string;
  number: number;
  title: string;
  requested_by_name: string | null;
  created_at: number;
}
export interface MyApprovalsResponse {
  approvals: PendingTicketApproval[];
  taskApprovals?: PendingTaskApproval[];
  kbApprovals?: PendingKbApproval[];
  changeApprovals?: PendingChangeApproval[];
}

/** Body for POST /api/kb/articles/:id/submit-for-review (0096). */
export interface SubmitKbReviewInput {
  approver_id: string;
  note?: string;
}

/** Task / action-item approval row (0082) — same shape as TicketApprovalRow. */
export interface TaskApprovalRow {
  id: string;
  task_id: string;
  approver_id: string | null;
  status: "pending" | "approved" | "denied";
  note: string | null;
  requested_by: string | null;
  created_at: number;
  decided_at: number | null;
  approver_name: string | null;
  requested_by_name: string | null;
}
export interface TaskApprovalResponse {
  approval?: TaskApprovalRow;
  ok?: boolean;
}

// ===== Facilities module (building-management request queue) ===============
// Mirrors the `facility-*` routes in workers/ac-edge/src/index.ts. Statuses /
// priorities / categories are fixed value sets for this module (see the
// FACILITY_* constants in lib/constants.ts).

export interface FacilityRequestRow {
  id: string;
  number: number;
  title: string;
  description: string | null;
  campus: string | null; // 0074: campuses-list code (top of the place cascade)
  building: string | null; // buildings-list code (child of campus)
  location: string | null; // rooms-list code (child of building)
  category: string | null; // hvac|plumbing|electrical|cleaning|safety|grounds|other
  status: string; // new|in_progress|on_hold|done|cancelled
  priority: string; // low|normal|high|urgent
  assignee_id: string | null;
  requester_name: string | null;
  requester_email: string | null;
  created_by: string | null;
  created_at: number;
  updated_at: number;
  /** 0039 CMMS work-order semantics + preventive-maintenance fields. */
  wo_type: string; // reactive|preventive|inspection
  due_date: number | null;
  scheduled_for: number | null;
  recurrence: string; // none|daily|weekly|monthly|quarterly|yearly
  asset_id: string | null;
  /** 0084: owning facility group (null = unscoped/visible to all). */
  group_id: string | null;
  /** Joined display name of the assignee; null when unassigned. */
  assignee_name?: string | null;
}

/** 0039: an ordered procedure/checklist step on a facility request. */
export interface FacilityChecklistRow {
  id: string;
  request_id: string;
  label: string;
  is_done: number; // 0/1
  position: number;
  created_at: number;
}

/** 0086: a per-work-order to-do task — assignable, due-dated, tri-state status.
 *  Distinct from FacilityChecklistRow (simple label+done). On the cross-WO "my
 *  tasks" feed, wo_number/wo_title carry the parent work order for linking. */
export interface FacilityRequestTaskRow {
  id: string;
  request_id: string;
  title: string;
  assignee_id: string | null;
  due_date: number | null;
  status: string; // todo | in_progress | done
  position: number;
  created_by: string | null;
  created_at: number;
  updated_at: number;
  assignee_name?: string | null;
  wo_number?: number;
  wo_title?: string;
}

/** 0039: a facility equipment/plant registry row. */
export interface FacilityAssetRow {
  id: string;
  name: string;
  asset_tag: string | null;
  category: string | null;
  building: string | null;
  location: string | null;
  status: string; // operational|down|maintenance|retired
  manufacturer: string | null;
  model: string | null;
  serial: string | null;
  notes: string | null;
  /** 0084: owning facility group (null = unscoped/visible to all). */
  group_id: string | null;
  created_by: string | null;
  created_at: number;
  updated_at: number;
}

/** Detail from GET /api/facility-assets/:id → { asset: {...} }, with linked requests. */
export interface FacilityAssetDetail extends FacilityAssetRow {
  requests: FacilityRequestRow[];
}

/** 0070: one downtime interval for an asset. `ended_at` is NULL while open. */
export interface FacilityAssetDowntimeRow {
  id: string;
  asset_id: string;
  work_order_id: string | null;
  reason: string | null;
  started_at: number;
  ended_at: number | null;
  created_by: string | null;
  created_at: number;
}

/** 0070: derived downtime summary over a trailing window (90 days). */
export interface FacilityAssetDowntimeStats {
  is_down: boolean;
  open: FacilityAssetDowntimeRow | null;
  total_downtime_secs: number;
  window_secs: number;
  availability_pct: number;
}

export interface FacilityAssetWorkOrdersResponse {
  requests: FacilityRequestRow[];
}
export interface FacilityAssetDowntimeResponse {
  intervals: FacilityAssetDowntimeRow[];
  stats: FacilityAssetDowntimeStats;
}
export interface FacilityDowntimeMutationResponse {
  interval: FacilityAssetDowntimeRow;
  stats: FacilityAssetDowntimeStats;
}

/** 0040: an inventory catalog part. `low_stock` is derived server-side
 *  (quantity_on_hand <= min_quantity) and returned as a SQLite 0/1. */
export interface FacilityPartRow {
  id: string;
  name: string;
  part_number: string | null;
  category: string | null;
  location: string | null;
  quantity_on_hand: number;
  min_quantity: number;
  /** 0072: reorder threshold; 0 disables low-stock alerting for this part. */
  reorder_point: number;
  unit: string | null;
  unit_cost_cents: number | null;
  supplier: string | null;
  /** 0085: structured supplier link (facility_vendors.id); supplier kept for compat. */
  vendor_id: string | null;
  notes: string | null;
  /** 0084: owning facility group (null = unscoped/visible to all). */
  group_id: string | null;
  created_by: string | null;
  created_at: number;
  updated_at: number;
  low_stock: number; // 0/1
  /** 0072 derived: 1 when reorder_point > 0 AND quantity_on_hand <= reorder_point. */
  below_reorder: number; // 0/1
}

/** 0040: a part consumed on a work order (snapshot name + quantity). */
export interface FacilityRequestPartRow {
  id: string;
  request_id: string;
  part_id: string;
  part_name: string;
  quantity: number;
  created_at: number;
}

/** 0071: a preventive-maintenance schedule template — a recurring work-order
 *  blueprint. `checklist` is the ordered list of step labels copied onto each
 *  generated work order. */
export interface FacilityPmTemplateRow {
  id: string;
  name: string;
  category: string | null;
  priority: string; // low|normal|high|urgent
  frequency: string; // none|daily|weekly|monthly|quarterly|yearly
  every_n: number;
  checklist: string[];
  active: number; // 0/1
  last_generated_at: number | null;
  created_by: string | null;
  created_at: number;
  updated_at: number;
  group_id: string | null;
}

export interface FacilityPmTemplatesResponse {
  templates: FacilityPmTemplateRow[];
}
export interface FacilityPmTemplateResponse {
  template: FacilityPmTemplateRow;
}
export interface FacilityPmGenerateResponse {
  created: number;
  requests: FacilityRequestRow[];
}

export interface CreateFacilityPmTemplateInput {
  name: string;
  category?: string | null;
  priority?: string;
  frequency?: string;
  every_n?: number;
  checklist?: string[];
  active?: boolean;
  group_id?: string | null;
}
export interface UpdateFacilityPmTemplateInput {
  name?: string;
  category?: string | null;
  priority?: string;
  frequency?: string;
  every_n?: number;
  checklist?: string[];
  active?: boolean;
  group_id?: string | null;
}

export interface FacilityRequestComment {
  id: string;
  request_id: string;
  author_id: string | null;
  author_name: string | null;
  body: string;
  created_at: number;
  /** 0107: 1 = internal (staff-only) note. Absent/0 on normal comments. */
  is_internal?: number;
  /** 0087: emoji reactions on this comment; absent on legacy responses. */
  reactions?: CommentReaction[];
}

/** 0087: one facility GROUP team-chat message (author_name joined server-side). */
export interface FacilityGroupMessageRow {
  id: string;
  group_id: string;
  author_id: string | null;
  author_name: string | null;
  body: string;
  created_at: number;
}
export interface FacilityGroupMessagesResponse {
  messages: FacilityGroupMessageRow[];
}

/** Detail returned by GET /api/facility-requests/:id → { request: {...} }. */
export interface FacilityRequestDetail extends FacilityRequestRow {
  comments: FacilityRequestComment[];
  attachments: AttachmentRow[];
  checklist: FacilityChecklistRow[];
  /** 0040: parts consumed on this work order. */
  parts: FacilityRequestPartRow[];
  assignee_name: string | null;
  created_by_name: string | null;
  /** Joined name of the linked asset; null when none. */
  asset_name: string | null;
  /** 0060: the FULL ordered assignee/requester sets (position 0 is primary). */
  assignees: { user_id: string; name: string }[];
  requesters: { id: string; name: string; email: string | null }[];
}

export interface FacilityRequestsResponse {
  requests: FacilityRequestRow[];
}
export interface FacilityRequestResponse {
  request: FacilityRequestRow;
}
export interface FacilityRequestDetailResponse {
  request: FacilityRequestDetail;
}
export interface FacilityCommentResponse {
  comment: FacilityRequestComment;
}

export interface FacilityAssetsResponse {
  assets: FacilityAssetRow[];
}
export interface FacilityAssetResponse {
  asset: FacilityAssetRow;
}
export interface FacilityAssetDetailResponse {
  asset: FacilityAssetDetail;
}

export interface FacilityPartsResponse {
  parts: FacilityPartRow[];
}
export interface FacilityPartResponse {
  part: FacilityPartRow;
}
export interface FacilityRequestPartsResponse {
  parts: FacilityRequestPartRow[];
}
export interface FacilityRequestPartResponse {
  part: FacilityRequestPartRow;
}

export interface FacilityPartFilter {
  q?: string;
  category?: string;
  lowStock?: boolean;
  /** 0072: keep only parts below their reorder_point. */
  belowReorder?: boolean;
}

export interface CreateFacilityPartInput {
  name: string;
  part_number?: string | null;
  category?: string | null;
  location?: string | null;
  quantity_on_hand?: number;
  min_quantity?: number;
  reorder_point?: number;
  unit?: string | null;
  unit_cost_cents?: number | null;
  supplier?: string | null;
  vendor_id?: string | null;
  notes?: string | null;
  /** 0084: owning facility group (null = unscoped/visible to all). */
  group_id?: string | null;
}

export interface UpdateFacilityPartInput {
  name?: string;
  part_number?: string | null;
  category?: string | null;
  location?: string | null;
  quantity_on_hand?: number;
  min_quantity?: number;
  reorder_point?: number;
  unit?: string | null;
  unit_cost_cents?: number | null;
  supplier?: string | null;
  vendor_id?: string | null;
  notes?: string | null;
  /** 0084: owning facility group (null = unscoped/visible to all). */
  group_id?: string | null;
}

export interface FacilityChecklistResponse {
  items: FacilityChecklistRow[];
}
export interface FacilityChecklistItemResponse {
  item: FacilityChecklistRow;
}

/** 0086: per-work-order to-do task responses + the cross-WO "my tasks" feed. */
export interface FacilityTasksResponse {
  tasks: FacilityRequestTaskRow[];
}
export interface FacilityTaskResponse {
  task: FacilityRequestTaskRow;
}

export interface FacilityAssetFilter {
  q?: string;
  status?: string;
  building?: string;
  category?: string;
}

export interface CreateFacilityAssetInput {
  name: string;
  asset_tag?: string | null;
  category?: string | null;
  building?: string | null;
  location?: string | null;
  status?: string;
  manufacturer?: string | null;
  model?: string | null;
  serial?: string | null;
  notes?: string | null;
  /** 0084: owning facility group (null = unscoped/visible to all). */
  group_id?: string | null;
}

export interface UpdateFacilityAssetInput {
  name?: string;
  asset_tag?: string | null;
  category?: string | null;
  building?: string | null;
  location?: string | null;
  status?: string;
  manufacturer?: string | null;
  model?: string | null;
  serial?: string | null;
  notes?: string | null;
  /** 0084: owning facility group (null = unscoped/visible to all). */
  group_id?: string | null;
}

export interface FacilityRequestFilter {
  q?: string;
  status?: string;
  priority?: string;
  category?: string;
  assignee?: string;
  building?: string;
}

export interface CreateFacilityRequestInput {
  title: string;
  description?: string | null;
  campus?: string | null; // 0074: campuses-list code (top of the place cascade)
  building?: string | null;
  location?: string | null; // rooms-list code (child of building)
  category?: string | null;
  status?: string;
  priority?: string;
  assignee_id?: string | null;
  requester_name?: string | null;
  requester_email?: string | null;
  /** 0060 multi-sets (full replace; position 0 becomes the primary). */
  assignee_ids?: string[];
  requesters?: { name: string; email?: string | null }[];
  /** Email notification opt-outs (default ON on create). */
  notify_requester?: boolean;
  notify_assignee?: boolean;
  wo_type?: string;
  due_date?: number | null;
  scheduled_for?: number | null;
  recurrence?: string;
  asset_id?: string | null;
  /** 0084: owning facility group (null = unscoped/visible to all). */
  group_id?: string | null;
}

// ===========================================================================
// Change Management (0091) — operational/IT change requests, a first-class work
// type (stable work-type id "change_management"). Mirrors the facility-request
// shapes. change_type/status/risk are fixed value sets (validated server-side).
// ===========================================================================
export type ChangeStatus =
  | "draft"
  | "in_review"
  | "approved"
  | "scheduled"
  | "implemented"
  | "closed"
  | "rejected";
export type ChangeRisk = "low" | "medium" | "high";
export type ChangeType = "standard" | "normal" | "emergency";

export interface ChangeRequestRow {
  id: string;
  number: number;
  title: string;
  description: string | null;
  change_type: string | null; // standard|normal|emergency
  category: string | null;
  risk: string; // low|medium|high
  status: string; // draft|in_review|approved|scheduled|implemented|closed|rejected
  requested_by: string | null;
  assignee_id: string | null;
  scheduled_start: number | null;
  scheduled_end: number | null;
  department_id: string | null; // owning Change-Mgmt department (0099)
  // ---- Extended intake fields (0103) ----
  target_date: number | null;
  notify_endusers: boolean;
  notification_dates: string | null;
  urgency: string | null; // high|moderate|low
  business_impact: string | null; // configurable (change.impacts)
  requires_approval: string | null; // yes|notification_only
  automated_recurring: boolean;
  schedule: string | null; // configurable (change.schedules)
  campuses_affected: string[]; // campus codes (shared facility.campuses)
  buildings_affected: string[]; // building codes (shared facility.buildings)
  contributors: string[]; // user IDs (multi-user picker)
  summary_of_change: string | null;
  testing_plan: string | null;
  is_budgeted: string | null; // yes|no|na
  created_by: string | null;
  created_at: number;
  updated_at: number;
  /** Joined display name of the assignee; null when unassigned. */
  assignee_name?: string | null;
  /** Joined department name; null when no department (0099). */
  department_name?: string | null;
}

export interface ChangeRequestComment {
  id: string;
  request_id: string;
  author_id: string | null;
  author_name: string | null;
  body: string;
  created_at: number;
}

export interface ChangeRequestAsset {
  asset_id: string;
  note: string | null;
  created_at: number;
  asset_name: string | null;
  asset_tag: string | null;
}

/** An IT-system link on a change request (0101). */
export interface ChangeRequestSystem {
  it_system_id: string;
  created_at: number;
  name: string | null;
}

/** One approver on a change request — independent decision per approver (0098). */
export interface ChangeApprover {
  id: string;
  request_id: string;
  user_id: string;
  status: "pending" | "approved" | "rejected";
  note: string | null;
  decided_at: number | null;
  created_at: number;
  user_name: string | null;
}

/** Detail returned by GET /api/change-requests/:id → { request: {...} }. */
export interface ChangeRequestDetail extends ChangeRequestRow {
  comments: ChangeRequestComment[];
  attachments: AttachmentRow[];
  affected_assets: ChangeRequestAsset[];
  systems: ChangeRequestSystem[];
  approvers: ChangeApprover[];
  assignee_name: string | null;
  requested_by_name: string | null;
  created_by_name: string | null;
}

/** One configurable change value-set option (code stable; label/color display). */
export interface ChangeOption {
  code: string;
  label: string;
  color?: string | null;
}
/** Tenant-configurable change-management value sets (Settings → Change Management). */
export interface ChangeConfig {
  statuses: ChangeOption[];
  types: ChangeOption[];
  risks: ChangeOption[];
  /** Automation/recurrence cadence (0103). */
  schedules: ChangeOption[];
  /** Business-impact levels (0103). */
  impacts: ChangeOption[];
  /** Urgency / priority levels (0103). */
  urgencies: ChangeOption[];
}
export interface ChangeApproversResponse {
  approvers: ChangeApprover[];
}

export interface ChangeRequestFilter {
  q?: string;
  status?: string;
  risk?: string;
  change_type?: string;
  assignee?: string;
  department_id?: string;
  urgency?: string;
  business_impact?: string;
}

export interface CreateChangeRequestInput {
  title: string;
  description?: string | null;
  change_type?: string | null;
  category?: string | null;
  risk?: string;
  status?: string;
  requested_by?: string | null;
  assignee_id?: string | null;
  scheduled_start?: number | null;
  scheduled_end?: number | null;
  department_id?: string | null;
  affected_asset_ids?: string[];
  system_ids?: string[];
  /** Approver user ids — auto-filled from the department but manually editable. */
  approver_ids?: string[];
  target_date?: number | null;
  notify_endusers?: boolean;
  notification_dates?: string | null;
  urgency?: string | null;
  business_impact?: string | null;
  requires_approval?: string | null;
  automated_recurring?: boolean;
  schedule?: string | null;
  campuses_affected?: string[] | null;
  buildings_affected?: string[] | null;
  contributors?: string[] | null;
  summary_of_change?: string | null;
  testing_plan?: string | null;
  is_budgeted?: string | null;
}

export interface UpdateChangeRequestInput {
  title?: string;
  description?: string | null;
  change_type?: string | null;
  category?: string | null;
  risk?: string;
  status?: string;
  requested_by?: string | null;
  assignee_id?: string | null;
  scheduled_start?: number | null;
  scheduled_end?: number | null;
  department_id?: string | null;
  affected_asset_ids?: string[];
  system_ids?: string[];
  target_date?: number | null;
  notify_endusers?: boolean;
  notification_dates?: string | null;
  urgency?: string | null;
  business_impact?: string | null;
  requires_approval?: string | null;
  automated_recurring?: boolean;
  schedule?: string | null;
  campuses_affected?: string[] | null;
  buildings_affected?: string[] | null;
  contributors?: string[] | null;
  summary_of_change?: string | null;
  testing_plan?: string | null;
  is_budgeted?: string | null;
}

/** A configurable IT System (Settings → IT Systems; 0101). */
export interface ItSystem {
  id: string;
  name: string;
  position: number;
  active: number; // 0/1
  created_at: number;
}
export interface ItSystemsResponse {
  it_systems: ItSystem[];
}

export interface ChangeRequestsResponse {
  requests: ChangeRequestRow[];
}
export interface ChangeRequestResponse {
  request: ChangeRequestRow;
}
export interface ChangeRequestDetailResponse {
  request: ChangeRequestDetail;
}
export interface ChangeRequestCommentResponse {
  comment: ChangeRequestComment;
}

/** A facilities group (campus/team) for scoped visibility (0084). */
export interface FacilityGroupRow {
  id: string;
  name: string;
  description: string | null;
  created_by: string | null;
  created_at: number;
  updated_at: number;
  member_count?: number;
}
export interface FacilityGroupsResponse {
  groups: FacilityGroupRow[];
}
export interface FacilityGroupMemberRow {
  group_id: string;
  user_id: string;
  role: "member" | "lead";
  created_at: number;
  name?: string | null;
  email?: string | null;
}
export interface FacilityGroupMembersResponse {
  members: FacilityGroupMemberRow[];
}

/** 0085: a vendor/supplier entity — models BOTH service vendors (linked to
 *  equipment) and part suppliers (referenced by FacilityPartRow.vendor_id). */
export interface FacilityVendorRow {
  id: string;
  name: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
  website: string | null;
  payment_terms: string | null;
  contract_start: number | null;
  contract_end: number | null;
  notes: string | null;
  created_by: string | null;
  created_at: number;
  updated_at: number;
  group_id: string | null;
}
export interface FacilityVendorsResponse {
  vendors: FacilityVendorRow[];
}
export interface FacilityVendorResponse {
  vendor: FacilityVendorRow;
}
export interface CreateFacilityVendorInput {
  name: string;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  address?: string | null;
  website?: string | null;
  payment_terms?: string | null;
  contract_start?: number | null;
  contract_end?: number | null;
  notes?: string | null;
  group_id?: string | null;
}
export interface UpdateFacilityVendorInput {
  name?: string;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  address?: string | null;
  website?: string | null;
  payment_terms?: string | null;
  contract_start?: number | null;
  contract_end?: number | null;
  notes?: string | null;
  group_id?: string | null;
}
/** A vendor↔asset link, with the vendor name joined (the equipment card). */
export interface FacilityAssetVendorRow {
  asset_id: string;
  vendor_id: string;
  is_primary: number; // 0/1
  note: string | null;
  created_at: number;
  vendor_name: string;
}
export interface FacilityAssetVendorsResponse {
  vendors: FacilityAssetVendorRow[];
}

export interface UpdateFacilityRequestInput {
  title?: string;
  description?: string | null;
  campus?: string | null; // 0074: campuses-list code (top of the place cascade)
  building?: string | null;
  location?: string | null; // rooms-list code (child of building)
  category?: string | null;
  status?: string;
  priority?: string;
  assignee_id?: string | null;
  requester_name?: string | null;
  requester_email?: string | null;
  /** 0060 multi-sets (full replace; position 0 becomes the primary). */
  assignee_ids?: string[];
  requesters?: { name: string; email?: string | null }[];
  /** Email notification opt-ins on update (default OFF). */
  notify_requester?: boolean;
  notify_assignee?: boolean;
  wo_type?: string;
  due_date?: number | null;
  scheduled_for?: number | null;
  recurrence?: string;
  asset_id?: string | null;
  group_id?: string | null;
}

// ---- Ticket flows (0109) ----
export type TicketFlowFieldType = "text" | "textarea" | "email" | "select" | "date" | "checkbox";
export interface TicketFlowField {
  key: string;
  label: string;
  type: TicketFlowFieldType;
  options?: string[];
  required?: boolean;
}
export interface TicketFlowCreateStepConfig {
  template_id: string;
  subject?: string;
  description?: string;
}
export interface TicketFlowEmailStepConfig {
  to: "initiator" | "custom";
  to_custom?: string;
  subject: string;
  body: string;
}
export interface TicketFlowStep {
  id: string;
  type: "create_ticket" | "send_email";
  label: string;
  config: TicketFlowCreateStepConfig | TicketFlowEmailStepConfig;
  condition?: { field_key: string; equals?: unknown };
  /** create_ticket only: id of an earlier create_ticket step whose ticket must
   *  close before this one is created. Empty = create immediately. */
  depends_on?: string | null;
}
export interface TicketFlow {
  id: string;
  name: string;
  description: string | null;
  is_active: number;
  intake_form: TicketFlowField[];
  steps: TicketFlowStep[];
  public_slug: string | null;
  public_enabled: number;
  brand_logo_url: string | null;
  brand_color: string | null;
  created_by: string | null;
  created_at: number;
  updated_at: number;
}
/** Anonymous share-page surface of a flow (no steps/ids — see /api/flow/:slug). */
export interface PublicTicketFlow {
  name: string;
  description: string | null;
  intake_form: TicketFlowField[];
  brand_logo_url: string | null;
  brand_color: string | null;
}
export interface TicketFlowRunStep {
  id: string;
  run_id: string;
  position: number;
  type: "create_ticket" | "send_email";
  label: string;
  status: "done" | "skipped" | "failed";
  ticket_id: string | null;
  detail: string | null;
  created_at: number;
}
export interface TicketFlowRun {
  id: string;
  flow_id: string;
  flow_name: string;
  initiated_by: string | null;
  initiated_by_name: string | null;
  requester_name: string | null;
  requester_email: string | null;
  form_data: Record<string, unknown>;
  status: "pending" | "completed" | "partial" | "failed";
  created_at: number;
  steps?: TicketFlowRunStep[];
}
export interface TicketFlowsResponse {
  flows: TicketFlow[];
}
export interface TicketFlowResponse {
  flow: TicketFlow;
}
export interface TicketFlowRunResponse {
  run: TicketFlowRun;
}
export interface TicketFlowRunsResponse {
  runs: TicketFlowRun[];
}
export interface TicketFlowRunDetailResponse {
  run: TicketFlowRun;
  steps: TicketFlowRunStep[];
}
